/**
 * Material UI Input Wrapper
 * Styles overriding using this documentation guide https://material-ui.com/customization/overrides/
 */
import React from "react"
import PropTypes from "prop-types"
import {
  FormIcon,
  Hint,
  Heading,
  SubHeading,
  OptionalWrapper,
  Message,
  IconWrapper,
  OKLength,
  BadLength,
  TooltipContainer,
  StyledFormControl,
  StyledFormControlMedium,
  StyledFormControlLarge,
  StyledFormControlXLarge,
  StyledFormControlXXLarge,
  StyledInputLabel,
  StyledInput,
} from "./styles"
import Select from "@material-ui/core//Select"
import Text from "components/Text"
import Icon from "components/Icon"
import {
  DEFAULT_MAX_CHARACTERS_INPUT_FIELD,
  DEFAULT_MAX_CHARACTERS,
} from "utils/constants"
import Tooltip from "components/Tooltip"
import Translate from "components/Translate/container"
import { connect } from "react-redux"
import { translateText } from "utils/languageUtils"

class InputField extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      touched: false,
      currentLength: 0,
    }
    this.labelRef = React.createRef()
  }

  componentWillUpdate(nextProps) {
    if (this.props.value && (!nextProps.value || nextProps.value === "")) {
      this.setState({ touched: false })
    }
  }

  onChange = ev => {
    const value = ev.target.value || ""
    if (!value) {
      this.props.onChange({
        target: {
          id: ev.target.id,
          value: "",
        },
      })
    }
    if (this.props.type === "number") {
      const stringValue = "" + value
      if (isNaN(value) || stringValue.indexOf("e") !== -1) {
        return false
      }
      if (this.props.onChange)
        this.props.onChange({
          target: {
            id: ev.target.id,
            value: parseFloat(stringValue),
          },
        })
    } else {
      const maxLength =
        this.props.maxLength || DEFAULT_MAX_CHARACTERS_INPUT_FIELD
      const currentLength = value.length
      this.setState({ currentLength: currentLength })
      if (this.props.maxWords) {
        if (
          value.split(" ").length <= this.props.maxWords &&
          value.length < DEFAULT_MAX_CHARACTERS
        ) {
          if (this.props.onChange) this.props.onChange(ev)
        }
      } else if (maxLength) {
        if (currentLength <= this.props.maxLength) {
          if (this.props.onChange) this.props.onChange(ev)
        }
      } else if (currentLength < maxLength) {
        if (this.props.onChange) this.props.onChange(ev)
      }
      return true
    }
  }

  render() {
    const {
      autoComplete,
      autoFocus,
      children,
      className,
      defaultValue,
      disableUnderline,
      error,
      errorDisplay,
      fullWidth,
      forceErrorDisplay,
      errorMessage,
      hint,
      hintSmall,
      id,
      iconName,
      InputLabelProps,
      inputProps,
      InputProps,
      inputRef,
      label,
      large,
      medium,
      xlarge,
      xxlarge,
      multiline,
      name,
      onBlur,
      onChange,
      onEnter,
      onFocus,
      onKeyPress,
      placeholder,
      required,
      readOnly,
      rows,
      rowsMax,
      select,
      SelectProps,
      success,
      successMessage,
      type,
      validationType,
      value,
      variant,
      heading,
      shrink,
      subheading,
      maxLength,
      showCountChar,
      tooltip,
      optional,
      translate = true,
      ...other
    } = this.props

    const { currentLength } = this.state

    const InputMore = {}

    // in case of validationType like onBlur we should display the error only if the onBlur function has been
    // called which turns the touched variable to true

    const hasError =
      validationType === "onSubmit" || forceErrorDisplay === true
        ? error
        : this.state.touched && error
    const helperTextId = errorMessage && id ? `${id}-helper-text` : undefined
    const InputElement = (
      <StyledInput
        style={{ paddingLeft: iconName ? 10 : 0 }}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        defaultValue={defaultValue}
        disableUnderline={disableUnderline}
        fullWidth={fullWidth}
        multiline={multiline}
        name={name}
        rows={rows}
        rowsMax={rowsMax}
        type={type === "number" ? "text" : type}
        value={value}
        id={id}
        inputRef={inputRef}
        readOnly={readOnly}
        onBlur={ev => {
          this.setState({ touched: true })
          if (onBlur) onBlur(ev)
        }}
        onKeyPress={ev => {
          if (ev.key === "Enter") {
            if (onEnter) onEnter(ev)
          } else {
            if (onKeyPress) onKeyPress(ev)
          }
        }}
        onChange={this.onChange}
        onFocus={onFocus}
        /* onEnter={ev=> {
          if(onEnter) onEnter(ev)
        }}*/
        placeholder={translateText(
          this.props.translationMap,
          placeholder,
          false
        )}
        inputProps={inputProps}
        {...InputMore}
        {...InputProps}
        error={hasError}
      />
    )

    const FormControlElement = medium
      ? StyledFormControlMedium
      : large
      ? StyledFormControlLarge
      : xlarge
      ? StyledFormControlXLarge
      : xxlarge
      ? StyledFormControlXXLarge
      : StyledFormControl

    const remainingText = <Translate>characters remaining</Translate>

    const numChars =
      showCountChar && value ? (
        currentLength <= maxLength ? (
          <OKLength>
            ({maxLength - currentLength + 1} {remainingText})
          </OKLength>
        ) : (
          <BadLength>
            ({maxLength - currentLength + 1} {remainingText})
          </BadLength>
        )
      ) : (
        ""
      )

    return (
      <div
        className={hasError ? "hasError" : null}
        style={{ flex: 1, display: "flex", flexDirection: "column" }}
        //style={{ width: "100%" }}
      >
        <div>
          {heading && (
            <Heading as={"label"} htmlFor={id} inlineLabels>
              <Translate>{heading}</Translate>
            </Heading>
          )}
          {subheading && (
            <SubHeading as={"div"} variant={"hint"} inlineLabels>
              <Translate>{subheading}</Translate>
            </SubHeading>
          )}
        </div>
        <FormControlElement
          aria-describedby={helperTextId}
          className={className}
          error={hasError}
          fullWidth={fullWidth}
          required={required}
          variant={variant}
          {...other}
        >
          {iconName && (
            <IconWrapper>
              <Icon variant={iconName} />
            </IconWrapper>
          )}

          {tooltip && (
            <TooltipContainer>
              <Tooltip tooltipId={tooltip} />
            </TooltipContainer>
          )}

          <StyledInputLabel
            shrink={shrink}
            htmlFor={id}
            ref={this.labelRef}
            style={{ paddingLeft: iconName ? "25px" : "10px" }}
            {...InputLabelProps}
          >
            {translate ? <Translate>{label}</Translate> : label}
            {optional && (
              <OptionalWrapper>
                {" "}
                (<Translate>Optional</Translate>)
              </OptionalWrapper>
            )}
          </StyledInputLabel>

          {select ? (
            <Select value={value} input={InputElement} {...SelectProps}>
              {children}
            </Select>
          ) : (
            InputElement
          )}

          {(validationType !== "onSubmit" &&
            this.state.touched &&
            success &&
            successMessage) ||
          (validationType === "onSubmit" && success && successMessage) ? (
            <Message hint={hint}>
              <Text variant={"successInputHelp"}>
                <Translate>{successMessage}</Translate>
              </Text>
              <FormIcon variant={"success"}>
                <Icon variant={"tick_circle"} />
              </FormIcon>
            </Message>
          ) : null}

          {(forceErrorDisplay && hasError && errorMessage) ||
          (validationType !== "onSubmit" &&
            this.state.touched &&
            hasError &&
            errorMessage) ||
          (validationType === "onSubmit" && hasError && errorMessage) ? (
            <Message hint={hint} errorDisplay={errorDisplay} id={`error-${id}`}>
              <Text variant={"errorInputHelp"}>
                {translate ? (
                  <Translate>{errorMessage}</Translate>
                ) : (
                  <>{errorMessage}</>
                )}
              </Text>
              <FormIcon variant={"danger"}>
                <Icon variant={"warning"} />
              </FormIcon>
            </Message>
          ) : null}

          {hint && (
            <Hint>{translate ? <Translate>{hint}</Translate> : { hint }}</Hint>
          )}

          {numChars && (
            <StyledInputLabel
              shrink={shrink}
              htmlFor={id}
              ref={this.labelRef}
              {...InputLabelProps}
            >
              {numChars}
            </StyledInputLabel>
          )}
        </FormControlElement>
      </div>
    )
  }
}

InputField.propTypes = {
  autoComplete: PropTypes.string,
  autoFocus: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  FormHelperTextProps: PropTypes.object,
  fullWidth: PropTypes.bool,
  errorMessage: PropTypes.node,
  id: PropTypes.string,
  InputLabelProps: PropTypes.object,
  InputProps: PropTypes.object,
  inputProps: PropTypes.object,
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  label: PropTypes.node,
  margin: PropTypes.oneOf(["none", "dense", "normal"]),
  multiline: PropTypes.bool,
  name: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  rows: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  rowsMax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  select: PropTypes.bool,
  SelectProps: PropTypes.object,
  shrink: PropTypes.bool,
  type: PropTypes.string,
  validationType: PropTypes.oneOf(["onBlur", "onSubmit"]),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool])
    ),
  ]),
  variant: PropTypes.oneOf(["standard"]),
}

InputField.defaultProps = {
  errorDisplay: "inline",
  required: false,
  select: false,
  fullWidth: true,
  maxLength: DEFAULT_MAX_CHARACTERS_INPUT_FIELD,
  medium: false,
  xlarge: false,
  variant: "standard",
  validationType: "onBlur",
  forceErrorDisplay: false,
}

const mapStateToProps = ({ data }) => ({
  translationMap: data.translationMap,
})

const mapDispatchToProps = dispatch => {}

export default connect(mapStateToProps, mapDispatchToProps)(InputField)
